/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState } from 'react';
import { jsx, css } from '@emotion/react';
import { Pivot, PivotItem, PrimaryButton, Text } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import SelectCobrandsForm from '../HubSettings/Forms/SelectCobrandsForm';
import ShowCobrandList from '../HubSettings/ShowCobrandList';
import FluentSelect from '../../../Forms/FluentSelect';
import SwapResourceDropdowns from './Helpers/SwapResourceDropdowns';
import { 
  headerCss, oneBtnCss, twoBtnCss, validationSchema, mapInitialValues, resourceTypes, successMsg, setErrorMsg, replaceModalExplain
} from './Helpers/CopyAndFunctions';
import ReplaceModal from '../../../Util/ReplaceModal';
import ErrorComponent from '../../../Util/ErrorComponent';
import SuccessComponent from '../../../Util/SuccessComponent';
import FeatureSummary from '../../../Util/FeatureSummary';

const featuredSwapSummary = [
    {
        selected: "0",
        label: "Select Cobrands",
        summary: "Choose the cobrands you want to update by selecting clients, cobrand tags, or individual cobrands. The selected cobrands will have their featured resource swapped.",
        imageUrlPath: "featured-swap-cobrands",
    },
    {
        selected: "1",
        label: "Select Resource Type",
        summary: "Choose the type of featured resource to swap (banners, courses, tools, videos, etc.). Only resources of the selected type will be available for swapping.",
        imageUrlPath: "featured-swap-resource-type",
    },
    {
        selected: "2",
        label: "Swap Resource",
        summary: "Select the resource to find and the resource to replace it with. The swap will update all selected cobrands that have the 'find' resource as featured.",
        imageUrlPath: "featured-swap-resource",
    },
];

const FeaturedSwapTool = () => {
  const [selectedKey, setSelectedKey] = useState('0');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceError, setReplaceError] = useState('');
  const [pendingValues, setPendingValues] = useState(null);
  const [{ loading: swapLoading }, swapContent] = useAxios({ url: '/internal/cobrand/featured-swap', method: 'POST' }, { manual: true });
  const [, getAffectedCobrands] = useAxios({}, { manual: true });

  const handleLinkClick = (item) => setSelectedKey(item.props.itemKey);

  // Show modal before swap
  const handleSubmit = async (values, { resetForm }) => {
    const affectedCobrandIds = await getAffectedCobrands({
      method: "get",
      url: `/internal/cobrand/featured/cobrands/${values.resourceType}/${values.findResource?.id}`,
    });
    const modalAffectedCobrands = (values.cobrandList.filter(cobrand => affectedCobrandIds.data.includes(cobrand.coBrandId))) || [];
    setPendingValues({ values, resetForm, modalAffectedCobrands });
    setShowReplaceModal(true);
    setReplaceError('');
  };

  // Called when user confirms in ReplaceModal
  const handleReplaceConfirmed = async () => {
    if (!pendingValues) return;
    const { values, resetForm } = pendingValues;
    setShowReplaceModal(false);
    setReplaceError('');
    const result = await swapContent({
      data: {
        cobrandIds: (values.cobrandList || []).map(x => x.coBrandId),
        resourceTypeId: values.resourceType,
        findResourceId: values.findResource?.id,
        replaceResourceId: values.replaceResource?.id
      }
    });

    if (result.status === 200 && result.data === 1) {
      setShowError(false);
      setShowSuccess(true);
      resetForm();
      setSelectedKey('0');
    } else {
      setShowError(true);
      setShowSuccess(false);
      setReplaceError('Swap failed');
    }

    setTimeout(() => {
        setShowError(false);
        setShowSuccess(false);
    }, 5000);
    setPendingValues(null);
  };

  return (
    <Formik
      initialValues={{...mapInitialValues()}}
      isInitialValid={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, values, setFieldValue, handleSubmit, resetForm }) => {
        const summaryObj = featuredSwapSummary.find(s => String(s.selected) === String(selectedKey));
        return (
        <Form css={css`max-width: 900px;`} onSubmit={handleSubmit}>
          <div css={headerCss}>
            <div className="mt-1">
              <h4>Featured Swap Tool</h4>
            </div>
            <span>Swap featured resources of same type for multiple clients, cobrand tags, or cobrands.</span>
          </div>
          <div css={css`width: 100%;`} className="mt-1">
            <Pivot selectedKey={selectedKey} onLinkClick={handleLinkClick}>
              <PivotItem headerText="Select Cobrands" itemKey={'0'}>
                <div css={css`margin: 25px 0;`}>
                    <SelectCobrandsForm width={600} featured={true} />
                    <ShowCobrandList
                        clients={values.client}
                        tags={values.cobrandTag}
                        cobrands={values.cobrand}
                        setParentCobrandList={cobrands => setFieldValue('cobrandList', cobrands)}
                        setTotalToUpdate={() => {}}
                    />
                </div>
                <div css={oneBtnCss}>
                  <PrimaryButton text="Next" onClick={() => setSelectedKey('1')} disabled={!(values.cobrandList && values.cobrandList.length > 0)} />
                </div>
              </PivotItem>
              <PivotItem headerText="Select Resource Type" itemKey={'1'}>
                <div css={css`margin: 25px 0; max-width: 300px;`}>
                  <FluentSelect
                    name="resourceType"
                    label="Resource Type"
                    options={resourceTypes.map(rt => ({ value: rt.id, label: rt.name }))}
                    isClearable={false}
                  />
                </div>
                <div css={twoBtnCss}>
                  <PrimaryButton text="Back" onClick={() => setSelectedKey('0')} />
                  <PrimaryButton text="Next" onClick={() => setSelectedKey('2')} disabled={!values.resourceType || !(values.cobrandList && values.cobrandList.length > 0)} />
                </div>
              </PivotItem>
              <PivotItem headerText="Swap Resource" itemKey={'2'}>
                {(values.resourceType === 0 || !values.resourceType || values.cobrandList.length < 2) ? (
                    <div css={css`margin: 25px 0;`}>
                        <Text variant="xLarge">
                            Please select a resource type and at least 2 cobrands.
                        </Text>
                    </div>
                ) : (
                  <SwapResourceDropdowns resourceType={values.resourceType} cobrandList={values.cobrandList} />
                )}
                <div css={twoBtnCss}>
                  <PrimaryButton text="Back" onClick={() => setSelectedKey('1')} />
                  <PrimaryButton
                    text={swapLoading ? 'Swapping...' : 'Swap'}
                    onClick={() => {
                      if (Object.keys(errors).length > 0) {
                        setShowError(true);
                      } else {
                        setShowError(false);
                        handleSubmit();
                      }
                    }}
                    type="button"
                    disabled={
                      !values.findResource?.id ||
                      !values.replaceResource?.id ||
                      swapLoading ||
                      !(values.cobrandList && values.cobrandList.length > 0) ||
                      !values.resourceType ||
                      values.resourceType === 0
                    }
                  />
                  <ReplaceModal
                    isOpen={showReplaceModal}
                    onDismiss={() => { setShowReplaceModal(false); setPendingValues(null); }}
                    onReplace={handleReplaceConfirmed}
                    resourceTypeName={resourceTypes.find(rt => rt.id === Number(values.resourceType))?.name || ''}
                    findResourceName={values.findResource?.name || ''}
                    replaceResourceName={values.replaceResource?.name || ''}
                    cobrandNames={(pendingValues?.modalAffectedCobrands || []).map(c => c.name)}
                    loading={swapLoading}
                    error={replaceError}
                    explainText={replaceModalExplain(resourceTypes.find(rt => rt.id === Number(values.resourceType))?.name || '')}
                  />
                </div>
              </PivotItem>
            </Pivot>
          </div>
          <SuccessComponent successMessage={successMsg} showSuccess={showSuccess} setShowSuccess={setShowSuccess} />
          <ErrorComponent errorMessage={setErrorMsg(showError, errors)} showError={showError} setShowError={setShowError} />
          {summaryObj && (
            <FeatureSummary
              label={summaryObj.label}
              summary={summaryObj.summary}
              imageUrlPath={summaryObj.imageUrlPath}
            />
          )}
        </Form>
      );}}
    </Formik>
  );
};

export default FeaturedSwapTool;
